import { withRouter } from "react-router-dom";
import { connect, useSelector, useDispatch } from "react-redux";
import { bindActionCreators } from "redux";
import CipHeader from "../Components/CipHeader";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import medpassService from "../services/emar-medpass-service";
import cogoToast from "cogo-toast";
import Loader from "react-loader-spinner";
import { selectUsers } from "../state/reducers/userSlice";
import { selectPatient, clearSelectedPatient } from "../state/reducers/emarSlice";
import React, { useEffect, useState } from "react";
import moment from "moment";
import LEFT_ARROW from "../assets/img/Left_Arrow.svg";
import RIGHT_ARROW from "../assets/img/Right_Arrow.svg";
import RIGHT_ARROW_INACTIVE from "../assets/img/Right_Arrow_Inactive.svg";
import emarMedpassService from "../services/emar-medpass-service";
import DOWNLOAD from "../assets/img/download.png";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const MedpassHistory = ({ history }) => {
  const { user } = useSelector(selectUsers);
  const { selectedPatient } = useSelector(selectPatient);
  const [patientsList, setPatientsList] = useState([]);
  const [filteredPatientsList, setFilteredPatientsList] = useState([]);
  const [patSelected, setPatSelected] = useState({});
  const [loading, setLoading] = useState(false);
  const [date, setDate] = useState(new Date());
  const [historyData, setHistoryData] = useState([]);
  const [marAddendumMedications, setMarAddendumMedications] = useState([]);
  const [medReasonList, setMedReasonList] = useState([]);
  const [duration, setDuration] = useState();
  const [searchInput, setSearchInput] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const dispatchEmar = useDispatch();

  // Parse patient name and create searchable data
  const parseAndPreparePatients = (patients) => {
    return patients.map(patient => {
      const parts = (patient.pat_name || '').split(',').map(p => p.trim());
      return {
        ...patient,
        lastName: parts[0] || '',
        firstName: parts[1] || '',
        accountNumber: parts[2] || patient.pat_acct_nubmer || ''
      };
    });
  };

  // Custom filter function
  const handlePatientSearch = (searchValue) => {
    setSearchInput(searchValue);

    if (!searchValue || searchValue.trim() === '') {
      setFilteredPatientsList(patientsList);
      setShowDropdown(patientsList.length > 0);
      setHighlightedIndex(0);
      return;
    }

    const searchLower = searchValue.toLowerCase().trim();

    if (/^\d+$/.test(searchLower)) {
      const filtered = patientsList.filter(patient =>
        (patient.accountNumber || '').toLowerCase().includes(searchLower)
      );
      setFilteredPatientsList(filtered);
      setShowDropdown(filtered.length > 0);
      setHighlightedIndex(0);
      return;
    }

    const searchTerms = searchLower.split(/[,\s]+/).filter(term => term.length > 0);

    if (searchTerms.length === 0) {
      setFilteredPatientsList(patientsList);
      setShowDropdown(patientsList.length > 0);
      setHighlightedIndex(0);
      return;
    }

    const filtered = patientsList.filter(patient => {
      const firstName = (patient.firstName || '').toLowerCase();
      const lastName = (patient.lastName || '').toLowerCase();

      if (searchTerms.length === 1) {
        const term = searchTerms[0];
        return firstName.includes(term) || lastName.includes(term);
      } else {
        return searchTerms.every(term =>
          firstName.includes(term) || lastName.includes(term)
        );
      }
    });

    setFilteredPatientsList(filtered);
    setShowDropdown(filtered.length > 0);
    setHighlightedIndex(0);
  };

  async function getPatHistory() {
    try {
      setLoading(true);
      setHistoryData([]);
      let resp = await medpassService.getEmarPatientHistoryInfoNew(
        user?.facility,
        patSelected.pat_id,
        moment(date).format("MM/DD/YYYY")
      );
      if (resp === "no content to show") {
        setLoading(false);
        const { hide } = cogoToast.error(
          "No Administered Medications Found For Selected Patient and Date",
          { hideAfter: 4, onClick: () => { hide(); } }
        );
        return;
      }
      if (Array.isArray(resp) && resp.length > 0) {
        setHistoryData(resp);
      }
      setLoading(false);
    } catch (e) {
      setLoading(false);
      const { hide } = cogoToast.error(
        "Error getting data. Please try after sometime.",
        { hideAfter: 15, onClick: () => { hide(); } }
      );
    }
  }

  async function getMarAddendumMedications() {
    try {
      setLoading(true);
      setMarAddendumMedications([]);
      let resp = await medpassService.getMarAddendumMedications(
        patSelected.pat_id,
        moment(date).format("MM/DD/YYYY")
      );
      setMarAddendumMedications(resp);
      setLoading(false);
    } catch (e) {
      setLoading(false);
      const { hide } = cogoToast.error(
        "Error getting data. Please try after sometime.",
        { hideAfter: 15, onClick: () => { hide(); } }
      );
    }
  }

  const getDefaultSettings = async () => {
    try {
      const resp = await medpassService.getDefaults(user?.facility);
      setMedReasonList(resp?.rejected_reasons);
    } catch (e) {
      const { hide } = cogoToast.error(
        "Error getting data. Please try after sometime.",
        { hideAfter: 15, onClick: () => { hide(); } }
      );
    }
  };

  const getPatients = async () => {
    try {
      setLoading(true);
      let resp = await emarMedpassService.getEmarPatientInfo(
        user?.facility,
        moment(date).format("MM/DD/YYYY")
      );
      if (resp && resp.length > 0) {
        const prepared = parseAndPreparePatients(resp);
        setPatientsList(prepared);
        setFilteredPatientsList(prepared);
        if (selectedPatient?.pat_id) {
          let found = prepared.find((item) => item.pat_id === selectedPatient.pat_id);
          if (found) setPatSelected(found);
        }
      } else {
        setPatientsList([]);
        setFilteredPatientsList([]);
      }
      setLoading(false);
    } catch (e) {
      setLoading(false);
      const { hide } = cogoToast.error(
        "Error getting data. Please try after sometime.",
        { hideAfter: 15, onClick: () => { hide(); } }
      );
    }
  };

  useEffect(() => {
    getPatients();
    return function cleanup() {
      dispatchEmar(clearSelectedPatient());
    };
  }, []);

  useEffect(() => {
    if (selectedPatient?.pat_id) setPatSelected(selectedPatient);
  }, [selectedPatient]);

  useEffect(() => {
    setDuration(new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate());
  }, [date]);

  useEffect(() => {
    if (patSelected?.pat_id && date) {
      getPatHistory();
      getMarAddendumMedications();
      getDefaultSettings();
    }
  }, [patSelected, date]);

  const handleDownload = async () => {
    if (!loading) {
      setLoading(true);
      const domElement = document.querySelector('#patHistory');
      html2canvas(domElement, {
        onclose: (document) => {
          document.querySelector('#save-button').style.visibility = 'hidden';
        }
      }).then((canvas) => {
        const imgData = canvas.toDataURL('image/jpeg');
        const pdf = new jsPDF('l', 'mm', 'a4');
        pdf.addImage(imgData, 'JPEG', 5, 5, 280, 90);
        pdf.save('MedpassHistory.pdf');
        setLoading(false);
      });
    }
  };

  const handleNavigation = async () => {
    try {
      await history.push("/medpass");
    } catch (e) {
      setLoading(false);
    }
  };

  // ── Clear chip: reset patient selection ──
  const handleClearPatient = (e) => {
    e.preventDefault();
    setPatSelected({});
    setSearchInput("");
    setFilteredPatientsList(patientsList);
    setShowDropdown(false);
    setHighlightedIndex(0);
  };

  // ── Keyboard navigation in dropdown ──
  const handleKeyDown = (e) => {
    if (!showDropdown || filteredPatientsList.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(i => Math.min(i + 1, filteredPatientsList.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const patient = filteredPatientsList[highlightedIndex];
      if (patient) {
        setPatSelected(patient);
        setSearchInput("");
        setShowDropdown(false);
        setHighlightedIndex(0);
      }
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  return (
    <>
      <CipHeader />

      <div className="bg-white w-11/12 mx-auto mt-6 mb-4 rounded shadow-md">
        <div className="tableHeader flex clearboth shadow-md py-8 pl-3">
          <div className="w-1/3 text-darkclr font-semibold leading-10">
            <span className="text-xl">MEDICATION HISTORY (MAR)</span>
          </div>

          <div className="w-2/3 sm:w-3/4 text-right font-semibold">
            {/* Download */}
            <span
              className="inline-block align-middle mr-2 cursor-pointer"
              onClick={() => handleDownload()}
            >
              <img className="h-9 w-9" alt="" src={DOWNLOAD} />
            </span>

            {/* Left arrow */}
            <span
              className="inline-block align-middle cursor-pointer"
              onClick={() => {
                let prev = moment(date).subtract(1, 'months');
                setDate(new Date(moment(prev).format('MM/DD/YYYY')));
              }}
            >
              <img className="h-9 w-9" alt="" src={LEFT_ARROW} />
            </span>

            {/* Date picker */}
            <span className="inline-block mr-6">
              <div>
                <DatePicker
                  placeholderText="MM/DD/YYYY"
                  selected={date}
                  dateFormat="MMM y"
                  showMonthYearPicker
                  onChange={(date) => {
                    setDate(moment(date, "MM-DD-YYYY").toDate());
                  }}
                  className="border focus:outline-none h-11 bg-header_bg text-white rounded-md pl-2 font-normal text-sm"
                />
              </div>
            </span>

            {/* Right arrow */}
            {new Date().getMonth() !== date.getMonth() ? (
              <span
                className="inline-block align-middle ml-2"
                onClick={() => {
                  let next = moment(date).add(1, 'months');
                  setDate(new Date(moment(next).format('MM/DD/YYYY')));
                }}
              >
                <img className="align-bottom h-9 w-9 cursor-pointer" alt="" src={RIGHT_ARROW} />
              </span>
            ) : (
              <span className="inline-block align-middle ml-2">
                <img className="align-bottom h-9 w-9" alt="" src={RIGHT_ARROW_INACTIVE} />
              </span>
            )}

            {/* ── Patient Search with Blue Chip ── */}
            <span className="w-1/4 inline-block mr-4 min-w-1/2 relative align-middle">
              {/*
                Wrapper div acts as the "input box".
                - When no patient selected → shows a plain <input>
                - When patient selected    → shows a blue pill/chip with × button
              */}
              <div
                className="border border-gray-300 focus-within:border-blue-400 h-11 w-full rounded bg-white flex items-center px-2 cursor-text"
                onClick={() => {
                  if (!patSelected?.pat_id) {
                    document.getElementById('patSearchInputHistory')?.focus();
                  }
                }}
              >
                {patSelected?.pat_id ? (
                  /* ✅ BLUE CHIP — visible after a patient is selected */
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      background: '#0c82f7',
                      color: '#fff',
                      borderRadius: '20px',
                      padding: '3px 8px 3px 12px',
                      fontSize: '13px',
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                      gap: '6px',
                      maxWidth: '100%',
                      overflow: 'hidden',
                    }}
                  >
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {patSelected.pat_name}
                    </span>
                    {/* × clear button inside the chip */}
                    <button
                      onMouseDown={handleClearPatient}
                      style={{
                        background: 'rgba(255,255,255,0.28)',
                        border: 'none',
                        color: '#fff',
                        borderRadius: '50%',
                        width: '18px',
                        height: '18px',
                        cursor: 'pointer',
                        fontSize: '11px',
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 0,
                        flexShrink: 0,
                        lineHeight: 1,
                      }}
                    >
                      ✕
                    </button>
                  </span>
                ) : (
                  /* Plain text input — shown when nothing is selected */
                  <input
                    id="patSearchInputHistory"
                    type="text"
                    autoComplete="off"
                    style={{
                      border: 'none',
                      outline: 'none',
                      flex: 1,
                      fontSize: '13px',
                      background: 'transparent',
                      color: '#1e293b',
                      minWidth: 0,
                    }}
                    placeholder="Search Patient (name or account)"
                    value={searchInput}
                    onChange={(e) => handlePatientSearch(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onFocus={() => {
                      setShowDropdown(true);
                      setHighlightedIndex(0);
                      if (!searchInput) setFilteredPatientsList(patientsList);
                    }}
                    onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
                  />
                )}
              </div>

              {/* Dropdown list */}
              {showDropdown && !patSelected?.pat_id && (
                <div className="absolute z-50 top-full left-0 right-0 bg-white border border-gray-300 rounded-md shadow-lg max-h-64 overflow-y-auto mt-1">
                  {filteredPatientsList.length > 0 ? (
                    filteredPatientsList.map((patient, index) => (
                      <div
                        key={patient.pat_id}
                        className="px-4 py-3 cursor-pointer text-sm font-semibold text-left"
                        style={{
                          backgroundColor: index === highlightedIndex ? '#0c82f7' : 'white',
                          color: index === highlightedIndex ? 'white' : 'black',
                        }}
                        onMouseEnter={() => setHighlightedIndex(index)}
                        onMouseLeave={() => setHighlightedIndex(index)}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          setPatSelected(patient);
                          setSearchInput("");
                          setShowDropdown(false);
                          setHighlightedIndex(0);
                        }}
                      >
                        {patient.pat_name}
                      </div>
                    ))
                  ) : (
                    <div className="px-4 py-3 text-sm text-gray-500 text-center">
                      No patients found
                    </div>
                  )}
                </div>
              )}
            </span>

            {/* Go to Medpass button */}
            <span className="w-1/4 mr-4 pl-2 inline-block align-middle">
              {user?.userRole !== 'PROVIDER' && (
                <button
                  className="rounded font-medium h-10 text-white w-auto px-3 bg-secondary bg-login_btn mb-3 mt-4 focus:outline-none"
                  onClick={() => handleNavigation()}
                >
                  Go to Medpass administration
                </button>
              )}
            </span>
          </div>
        </div>

        <div id="patHistory" className="p-2 mt-3">
          {/* Legend */}
          <div className="w-full flex justify-end">
            <div className="emarAdministeredImg"></div>
            <span className="text-semibold text-sm ml-2 font-semibold mr-6">Administered</span>
            <div className="emarSelfAdministeredImg"></div>
            <span className="text-semibold text-sm ml-2 font-semibold mr-6">Self Administered</span>
            <div className="emarNotAdministeredImg"></div>
            <span className="text-semibold text-sm ml-2 font-semibold mr-6">Not Administered</span>
            <div className="emarRefusedImg"></div>
            <span className="text-semibold text-sm ml-2 font-semibold mr-6">Refused</span>
            <div className="emarHoldImg"></div>
            <span className="text-semibold text-sm ml-2 font-semibold mr-6">On Hold</span>
            <div className="emarDiscontinueImg"></div>
            <span className="text-semibold text-sm ml-2 font-semibold mr-6">Discontinue</span>
            <div className="emarNoShowImg"></div>
            <span className="text-semibold text-sm ml-2 font-semibold mr-6">No Show</span>
            <div className="emarKCAdministeredImg"></div>
            <span className="text-semibold text-sm ml-2 font-semibold mr-6">KOP continuation</span>
          </div>

          {/* Loader */}
          <div className={`inline-block customtop absolute z-50 ${loading ? "show" : "hidden"}`}>
            <Loader type="Oval" color="#00BFFF" height={50} width={50} timeout={300000} />
          </div>

          {Object.keys(patSelected).length > 0 && (
            <div className="p-3 w-full gap-y-4">
              {/* Medications Section */}
              <div className="bg-white rounded-lg shadow-xl p-4 mb-4 mx-4">
                <div className="space-y-4">
                  <div className="grey flex rounded-3xl flex-auto">
                    <div className="mr-4">
                      <img className="rounded-full w-16 h-16" alt="" src={patSelected?.profile_pic} />
                    </div>
                    <div className="pt-3">
                      <div className="w-1/50">
                        <span className="text-2xl font-semibold">{patSelected?.pat_name} </span>
                        <span className="text-sm font-semibold">{patSelected?.pat_gender} </span>.
                        <span className="text-sm font-semibold"> {patSelected?.pat_age} </span>.
                        <span className="text-sm font-semibold"> DOB : {moment.utc(patSelected?.pat_dob).format("MM/DD/YYYY")}</span>
                      </div>
                    </div>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="mt-8 w-full leading-normal">
                      <thead className="w-full">
                        <tr className="w-full">
                          <th className="w-96"></th>
                          {[...Array(28)].map((_, i) => (
                            <th key={i + 1} className="w-1/50">{i + 1}</th>
                          ))}
                          {duration > 28 && <th className="w-1/50">29</th>}
                          {duration > 29 && <th className="w-1/50">30</th>}
                          {duration > 30 && <th className="w-1/50">31</th>}
                        </tr>
                      </thead>
                      <tbody className="pt-3">
                        {historyData?.map((medi, index) => (
                          <React.Fragment key={medi.drG_ID + index + medi.drG_DNAME + 'Emar'}>
                            <tr>
                              <td colSpan="30" className="text-lg text-left pr-4 pt-4 font-semibold">
                                {medi?.drG_DNAME}
                                <div className="text-sm">{medi?.rxF_SIG}</div>
                                <div className="text-sm">
                                  {medi?.rxF_ORG_DATE && `Start date: ${moment.utc(medi?.rxF_ORG_DATE).format('MM/DD/YYYY')}`}
                                  {medi?.rxF_EXP_DATE && `   Stop date: ${moment.utc(medi?.rxF_EXP_DATE).format('MM/DD/YYYY')}`}
                                </div>
                                {medi?.is_stat_med && <div className="text-sm">STAT</div>}
                              </td>
                            </tr>
                            {medi?.ranges.map(item => (
                              <tr key={item.range + 'Emar'}>
                                <td className="text-base text-right pr-4 pt-2 font-normal">{item.range}</td>
                                {item?.status.map(dates => {
                                  let title = "";
                                  if (dates.administer_fname) title += "Nurse : " + dates.administer_fname + ' ' + dates.administer_lname + '\n';
                                  if (dates.administered_at) title += "Date : " + moment(dates.administered_at).format('MM-DD-YYYY HH:mm:ss') + '\n';
                                  if (dates.blood_sugar) title += "Blood Sugar : " + dates.blood_sugar + '\n';
                                  if (dates.diastolic) title += "Diastolic : " + dates.diastolic + '\n';
                                  if (dates.systolic) title += "Systolic : " + dates.systolic + '\n';
                                  if (dates.heart_rate) title += "Heart Rate : " + dates.heart_rate + '\n';
                                  if (dates.units_inj) title += "Units Injected : " + dates.units_inj + '\n';
                                  if (dates.medpass_status === 'K' || dates.medpass_status === 'P') title += "Qty given: " + dates.dose_taken_qty + '\n';
                                  let curDate = moment().format('MM/DD/YYYY');
                                  let medDate = moment(dates.med_date).format('MM/DD/YYYY');
                                  let isBefore24Hours = moment(medDate).isBefore(moment(curDate).subtract(24, 'hours'));
                                  return (
                                    <td
                                      key={dates?.medpass_status + title + `${dates?.is_disabled}` + 'Emar'}
                                      title={title}
                                      className="text-center pt-2"
                                    >
                                      <div className={`
                                        ${(moment(medDate).isAfter(curDate) || dates?.medpass_status === null) ? "bg-slate"
                                          : (dates?.medpass_status === "A" || dates?.medpass_status === "P" || dates?.medpass_status === "K") ? "emarAdministeredImg cursor-pointer mx-auto"
                                          : dates?.medpass_status === "S" ? "emarSelfAdministeredImg cursor-pointer mx-auto"
                                          : dates?.medpass_status === "H" ? "emarHoldImg cursor-pointer mx-auto"
                                          : (dates?.medpass_status === "C" || (dates?.medpass_status === '' && isBefore24Hours)) ? "emarNoShowImg cursor-pointer mx-auto"
                                          : dates?.medpass_status === "DC" ? "emarDiscontinueImg cursor-pointer mx-auto"
                                          : dates?.medpass_status === "N" ? "emarNotAdministeredImg cursor-pointer mx-auto"
                                          : dates?.medpass_status === "KC" ? "emarKCAdministeredImg cursor-pointer mx-auto"
                                          : (dates?.medpass_status === "R" || dates?.medpass_status === "D") ? "emarRefusedImg cursor-pointer mx-auto"
                                          : "shadow-sm mt-1 inline-block w-2 mb-1 border-2"
                                        }`}
                                      > </div>
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Addendum Medications */}
              {marAddendumMedications.length > 0 && (
                <div className="bg-white rounded-lg shadow-xl p-4 mb-4 mx-4">
                  <div className="space-y-4">
                    <div className="flex justify-between items-center cursor-pointer">
                      <h2 className="text-lg font-semibold">ADDENDUM MEDICATIONS</h2>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="mt-8 w-full border border-gray-200 shadow-sm rounded-lg">
                        <thead className="bg-gray-100 top-0 z-10">
                          <tr>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b">Med Date</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b">Range</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b">Rx Number</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b">Drug</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b">Status</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b">Administered At</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b">Nurse</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b">Notes</th>
                          </tr>
                        </thead>
                        <tbody className="pt-3">
                          {marAddendumMedications?.map((medi, index) => {
                            const medDate = medi?.med_date ? moment.utc(medi.med_date).format('MM-DD-YYYY') : "";
                            const administeredAt = medi?.administered_at ? moment(medi.administered_at).format('MM-DD-YYYY HH:mm:ss') : "";
                            const drug = `${medi?.drg_name || ""} ${medi?.drg_strength || ""} ${medi?.drg_dos_id || ""}`.trim();
                            return (
                              <tr key={index} className={`border-b ${index % 2 === 0 ? "bg-white" : "bg-gray-50"}`}>
                                <td className="px-4 py-3 text-sm text-gray-800">{medDate}</td>
                                <td className="px-4 py-3 text-sm text-gray-800">{medi?.range}</td>
                                <td className="px-4 py-3 text-sm text-gray-800">{medi?.rx_number}</td>
                                <td className="px-4 py-3 text-sm text-gray-800">{drug}</td>
                                <td className="px-4 py-3 text-sm text-gray-800">
                                  <div className="relative inline-block group">
                                    <div className={`
                                      ${(medi?.status === "A" || medi?.status === "P" || medi?.status === "K") ? "emarAdministeredImg cursor-pointer mx-auto"
                                        : medi?.status === "S" ? "emarSelfAdministeredImg cursor-pointer mx-auto"
                                        : medi?.status === "H" ? "emarHoldImg cursor-pointer mx-auto"
                                        : (medi?.status === "C" || medi?.status === '') ? "emarNoShowImg cursor-pointer mx-auto"
                                        : medi?.status === "DC" ? "emarDiscontinueImg cursor-pointer mx-auto"
                                        : medi?.status === "N" ? "emarNotAdministeredImg cursor-pointer mx-auto"
                                        : medi?.status === "KC" ? "emarKCAdministeredImg cursor-pointer mx-auto"
                                        : (medi?.status === "R" || medi?.status === "D") ? "emarRefusedImg cursor-pointer mx-auto"
                                        : "shadow-sm mt-1 inline-block w-2 mb-1 border-2"
                                      }`}
                                    > </div>
                                    <div className="pointer-events-none absolute left-1/2 top-full z-50 mt-2 w-max -translate-x-1/2 transform whitespace-nowrap rounded-lg px-3 py-2 text-sm text-white shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 bg-clarifications_sub">
                                      <p className="leading-tight">Administered By: {(medi?.administer_fname || "") + " " + (medi?.administer_lname || "")}</p>
                                      <p className="leading-tight">Administered At: {moment(medi?.administered_at).format('MM-DD-YYYY HH:mm:ss')}</p>
                                      {medi?.status === 'A' && <p className="leading-tight">Qty given: {medi?.dose_taken_qty}</p>}
                                      {medi?.status === 'N' && (
                                        <p className="leading-tight">Rejected Reason: {medReasonList.find(r => r.id === medi?.med_reason_id)?.reason || ''}</p>
                                      )}
                                      <span className="absolute -top-1 left-1/2 h-3 w-3 -translate-x-1/2 rotate-45 bg-gray-900" />
                                    </div>
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-800">{administeredAt}</td>
                                <td className="px-4 py-3 text-sm text-gray-800">{(medi?.administer_fname || "") + " " + (medi?.administer_lname || "")}</td>
                                <td className="px-4 py-3 text-sm text-gray-800">{medi?.notes}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default withRouter(
  connect(
    () => ({}),
    (dispatch) => bindActionCreators({}, dispatch)
  )(MedpassHistory)
);